import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, SlidersHorizontal, LineChart, Megaphone, Lock } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import {
  DEFAULT_PREFS,
  setDefaultConsent,
  saveConsent,
  loadConsent,
  CONSENT_EVENT,
} from "../lib/consent";

const CATEGORY_ICONS = {
  necessary: ShieldCheck,
  preferences: SlidersHorizontal,
  analytics: LineChart,
  marketing: Megaphone,
};
const CATEGORY_KEYS = ["necessary", "preferences", "analytics", "marketing"];
const ease = [0.22, 1, 0.36, 1];

function CookieIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="ck-grad" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop stopColor="#08AC7D" />
          <stop offset="1" stopColor="#4DD79A" />
        </linearGradient>
        <mask id="ck-bite">
          <rect width="64" height="64" fill="#fff" />
          <circle cx="55" cy="12" r="11" fill="#000" />
        </mask>
      </defs>
      <circle cx="30" cy="34" r="24" fill="url(#ck-grad)" mask="url(#ck-bite)" />
      <circle cx="24" cy="27" r="3.2" fill="#065F46" opacity="0.55" />
      <circle cx="37" cy="38" r="3.6" fill="#065F46" opacity="0.55" />
      <circle cx="26" cy="43" r="2.6" fill="#065F46" opacity="0.5" />
      <circle cx="41" cy="27" r="2.4" fill="#065F46" opacity="0.5" />
      <circle cx="20" cy="37" r="2" fill="#065F46" opacity="0.5" />
    </svg>
  );
}

function Toggle({ checked, disabled, onChange, testId, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testId}
      className={`focus-ring relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
        checked ? "bg-[#08AC7D]" : "bg-black/15"
      } ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-[22px]" : "translate-x-[2px]"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const cardRef = useRef(null);
  const lastFocused = useRef(null);

  // First load: set safe defaults, then decide whether to show the modal.
  useEffect(() => {
    setDefaultConsent();
    const stored = loadConsent();
    if (stored) {
      setPrefs({ ...DEFAULT_PREFS, ...stored.prefs, necessary: true });
      saveConsent({ ...DEFAULT_PREFS, ...stored.prefs, necessary: true });
    } else {
      const id = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(id);
    }
  }, []);

  // Re-open from footer / privacy links.
  useEffect(() => {
    const handler = () => {
      const stored = loadConsent();
      setPrefs(stored ? { ...DEFAULT_PREFS, ...stored.prefs, necessary: true } : DEFAULT_PREFS);
      setOpen(true);
    };
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  const persist = useCallback((next) => {
    saveConsent({ ...next, necessary: true });
    setOpen(false);
  }, []);

  const acceptAll = () => persist({ necessary: true, preferences: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, preferences: false, analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  // Focus management + trap + ESC (= reject non-essential, a valid choice).
  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement;
    const node = cardRef.current;
    const focusables = () =>
      node ? node.querySelectorAll('button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])') : [];
    const first = focusables()[0];
    if (first) first.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        rejectAll();
        return;
      }
      if (e.key === "Tab") {
        const items = focusables();
        if (items.length === 0) return;
        const firstEl = items[0];
        const lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (lastFocused.current && lastFocused.current.focus) lastFocused.current.focus();
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="cookie-consent"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
          <motion.div
            ref={cardRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-title"
            aria-describedby="cookie-desc"
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.38, ease }}
            className="relative z-10 flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-[0_40px_120px_-24px_rgba(0,0,0,0.45)]"
          >
            <div className="overflow-y-auto p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EAF7F0]">
                <CookieIcon className="h-6 w-6" />
              </div>
              <h2 id="cookie-title" className="mt-4 text-xl font-semibold tracking-tight text-black">
                {t("cookie.title")}
              </h2>
              <p id="cookie-desc" className="mt-2 text-sm leading-relaxed text-black/60">
                {t("cookie.intro")}
              </p>

              <div className="mt-5 space-y-2">
                {CATEGORY_KEYS.map((key) => {
                  const Icon = CATEGORY_ICONS[key];
                  const locked = key === "necessary";
                  return (
                    <div
                      key={key}
                      className="flex items-start gap-3 rounded-2xl border border-black/[0.07] bg-white p-3"
                      data-testid={`cookie-cat-${key}`}
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-black/[0.04] text-black">
                        <Icon strokeWidth={1.9} style={{ width: 16, height: 16 }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-black">{t(`cookie.categories.${key}.name`)}</h3>
                          {locked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF7F0] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#08AC7D]">
                              <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
                              {t("cookie.required")}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs leading-relaxed text-black/50">
                          {t(`cookie.categories.${key}.desc`)}
                        </p>
                      </div>
                      <div className="flex-shrink-0 pt-0.5">
                        <Toggle
                          checked={locked ? true : prefs[key]}
                          disabled={locked}
                          onChange={(v) => setPrefs((p) => ({ ...p, [key]: v }))}
                          testId={`cookie-toggle-${key}`}
                          label={t(`cookie.categories.${key}.name`)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="mt-4 text-xs leading-relaxed text-black/40">
                {t("cookie.footnote")}{" "}
                <Link to={`/${locale}/cookies`} className="font-medium text-[#08AC7D] underline underline-offset-2">
                  {t("cookie.policyLink")}
                </Link>
              </p>
            </div>

            <div className="border-t border-black/[0.06] bg-white p-5">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="focus-ring brand-gradient w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]"
              >
                {t("cookie.acceptAll")}
              </button>
              <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                  className="focus-ring rounded-full border border-black/15 px-5 py-3 text-sm font-semibold text-black transition-colors duration-200 hover:bg-black/[0.03]"
                >
                  {t("cookie.rejectAll")}
                </button>
                <button
                  type="button"
                  onClick={savePrefs}
                  data-testid="cookie-save"
                  className="focus-ring rounded-full border border-black/15 px-5 py-3 text-sm font-semibold text-black transition-colors duration-200 hover:bg-black/[0.03]"
                >
                  {t("cookie.save")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
